import React, { useEffect, useMemo, useState, useCallback } from "react";
import { roundSize } from "../../utils/money";
import { auth } from "../../firebase";
import { db } from "../../firebase";
import { DPI } from "../../config/dtfConfig";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  getMetadata,
} from "firebase/storage";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import "./ImageControlPanel.scss";
import { HiFolder, HiStar } from "react-icons/hi2";

// Debug log helper
function dlog(...args) {
  if (typeof window !== "undefined" && window.DEBUG_RECENT_UPLOADS) {
    // eslint-disable-next-line no-console
    console.debug("[RecentUploadsDialog]", ...args);
  }
}

// normalize tokenized Firebase download URLs so favorites match across token refreshes
const normUrl = (u) => {
  try {
    const x = new URL(String(u));
    x.search = ""; // drop ?token=...
    return x.toString();
  } catch {
    return String(u).split("?")[0];
  }
};

/**
 * RecentUploadsDialog (no MUI)
 * - Pure HTML + classes styled by ImageControlPanel.scss
 * - Uses ihx-card grid (same look as ImageHistory)
 * - Lets user browse folders under pictures/{uid}/ and search
 */
export default function RecentUploadsDialog({ open, onClose, onSelect }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [currentPrefix, setCurrentPrefix] = useState(""); // e.g. "", "jobs/2025/Aug"
  const [folders, setFolders] = useState(["All", "Unsorted"]); // Firestore folder names
  const [storageFolders, setStorageFolders] = useState([]); // [{name}] from Storage prefixes
  const [files, setFiles] = useState([]); // {name,url}
  const [folderMap, setFolderMap] = useState({}); // url -> folder name
  const [selectedFolder, setSelectedFolder] = useState("All");
  const [favorites, setFavorites] = useState(new Set()); // urls marked favorite
  // Sorting by last modified and name
  const [sort, setSort] = useState("newest"); // newest | oldest | az | za
  const [sortOpen, setSortOpen] = useState(false);
  const sortWrapRef = React.useRef(null);
  // Use normalized URL as the key for favorites
  const favKey = (f) =>
    typeof f === "string" ? normUrl(f) : f?.urlNorm || normUrl(f?.url);
  const isFav = (f) => {
    const key = favKey(f);
    const has = favorites.has(key);
    dlog("isFav?", { key, has });
    return has;
  };
  const user = auth.currentUser;

  const resetAndClose = useCallback(() => {
    dlog("resetAndClose called");
    setSearch("");
    setCurrentPrefix("");
    setFolders([]);
    setFiles([]);
    setLoading(false);
    setError("");
    onClose && onClose();
  }, [onClose]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    dlog("useEffect[ESC] entry");
    const onKey = (e) => {
      if (e.key === "Escape") resetAndClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      dlog("useEffect[ESC] exit");
    };
  }, [open, resetAndClose]);

  // Close sort menu on outside click or ESC
  useEffect(() => {
    const onDoc = (e) => {
      if (!sortWrapRef.current) return;
      if (!sortWrapRef.current.contains(e.target)) setSortOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setSortOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Build storage path for the current folder
  const storagePath = useMemo(() => {
    if (!user) return null;
    const base = `pictures/${user.uid}`;
    return currentPrefix ? `${base}/${currentPrefix}` : base;
  }, [user, currentPrefix]);

  // Load list for current folder
  useEffect(() => {
    dlog("useEffect[storage] entry", { open, user: !!user, storagePath });
    if (!open || !user || !storagePath) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const storage = getStorage();
        const listRef = ref(storage, storagePath);
        const res = await listAll(listRef);

        const nextFolders = res.prefixes.map((p) => ({ name: p.name }));
        // Fetch URLs and metadata for files in parallel
        const nextFiles = await Promise.all(
          res.items.map(async (it) => {
            const [url, meta] = await Promise.all([
              getDownloadURL(it),
              getMetadata(it).catch(() => null),
            ]);
            const lastMs = meta?.updated
              ? Date.parse(meta.updated)
              : meta?.timeCreated
                ? Date.parse(meta.timeCreated)
                : 0;
            const customWidthIn = meta?.customMetadata?.widthIn;
            const customHeightIn = meta?.customMetadata?.heightIn;
            return {
              name: it.name,
              url,
              urlNorm: normUrl(url),
              path: it.fullPath, // stable key
              lastMs, // storage last modified (ms since epoch)
              widthIn: customWidthIn,
              heightIn: customHeightIn,
            };
          })
        );

        if (!mounted) return;
        dlog("setStorageFolders", nextFolders);
        setStorageFolders(nextFolders);
        dlog("setFiles", nextFiles);
        setFiles(nextFiles);
      } catch (e) {
        if (!mounted) return;
        console.error("[RecentUploadsDialog] listAll failed", e);
        setError("Could not load this folder.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      dlog("useEffect[storage] exit");
    };
  }, [open, user, storagePath]);

  // Load Firestore-managed folders and URL→folder mappings (top-level)
  useEffect(() => {
    dlog("useEffect[firestore] entry", { open, user: !!user });
    if (!open || !user) return;
    const uid = user.uid;
    let mounted = true;
    (async () => {
      try {
        // Folders
        const fSnap = await getDocs(
          collection(db, "imageFolders", uid, "folders")
        );
        const names = [
          "All",
          "Unsorted",
          ...fSnap.docs.map((d) => d.data().name).filter(Boolean),
        ];
        if (mounted) {
          dlog("setFolders (firestore)", names);
          setFolders(Array.from(new Set(names)));
        }

        // Items mapping (url/path -> folder) and favorites (normalized URL only)
        const iSnap = await getDocs(
          collection(db, "imageFolders", uid, "items")
        );
        const map = {};
        const favs = new Set();
        iSnap.forEach((d) => {
          const data = d.data();
          dlog("doc", d.id, data);
          const { url, path, folder, favorite } = data;
          if (url && folder) map[url] = folder;
          if (path && folder) map[path] = folder; // keep path mapping for folder lookup, but NOT for favorites
          if (favorite && url) {
            favs.add(normUrl(url));
          }
        });
        dlog("setFolders (again)", names);
        setFolders(Array.from(new Set(names)));
        dlog("setFolderMap", map);
        setFolderMap(map);
        dlog("setFavorites", favs);
        setFavorites(favs);
        console.log("Found favorites (urls only):", favs);
      } catch (e) {
        console.error(
          "[RecentUploadsDialog] load Firestore folders/items failed",
          e
        );
      }
    })();
    return () => {
      mounted = false;
      dlog("useEffect[firestore] exit");
    };
  }, [open, user]);
  // Log all state values after state declarations and on any render
  useEffect(() => {
    dlog("state snapshot", {
      search,
      currentPrefix,
      selectedFolder,
      folders: folders.length,
      files: files.length,
      favorites: favorites.size,
    });
  });

  // Breadcrumbs from currentPrefix
  const crumbs = useMemo(() => {
    const parts = currentPrefix ? currentPrefix.split("/").filter(Boolean) : [];
    const acc = [];
    return [
      { label: "Home", to: "" },
      ...parts.map((p, idx) => {
        acc.push(p);
        return { label: p, to: acc.join("/") };
      }),
    ];
  }, [currentPrefix]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    // 1) Build base list with derived folder + fav flag
    const baseFiles = files.map((f) => {
      const key = f.urlNorm || normUrl(f.url);
      const folder = folderMap[f.path] || folderMap[f.url] || "Unsorted";
      const fav = favorites.has(key);
      return { ...f, key, folder, fav };
    });

    dlog("filter: baseFiles", baseFiles.length);

    // 2) Apply folder/fav filter
    let visFiles;
    if (selectedFolder === "__FAVORITES__") {
      visFiles = baseFiles.filter((f) => f.fav);
    } else if (selectedFolder === "All") {
      visFiles = baseFiles;
    } else {
      visFiles = baseFiles.filter((f) => f.folder === selectedFolder);
    }
    dlog("filter: after folder/fav", visFiles.length, selectedFolder);

    // 3) Apply search (by file name; extend as needed)
    if (q) {
      visFiles = visFiles.filter((f) => f.name.toLowerCase().includes(q));
    }
    dlog("filter: after search", visFiles.length, { q });

    // 4) Sort according to selection (default newest by last modified)
    const sorted = [...visFiles].sort((a, b) => {
      if (sort === "newest") return (b.lastMs || 0) - (a.lastMs || 0);
      if (sort === "oldest") return (a.lastMs || 0) - (b.lastMs || 0);
      if (sort === "az") return (a.name || "").localeCompare(b.name || "");
      if (sort === "za") return (b.name || "").localeCompare(a.name || "");
      return 0;
    });
    dlog(
      "filter: after sort",
      sorted.length,
      sort,
      sorted[0] && { name: sorted[0].name, lastMs: sorted[0].lastMs }
    );

    // 5) Storage subfolders (for breadcrumb browsing UI) — search by folder name only
    const fFolders = q
      ? storageFolders.filter((f) => f.name.toLowerCase().includes(q))
      : storageFolders;

    // Final log + return
    console.log("[RecentlyUploaded] visible after filter:", sorted.length, {
      favCount: favorites.size,
      sample: sorted[0] && {
        name: sorted[0].name,
        folder: sorted[0].folder,
        fav: sorted[0].fav,
        key: sorted[0].key,
      },
    });

    return { folders: fFolders, files: sorted };
  }, [
    search,
    files,
    selectedFolder,
    storageFolders,
    folderMap,
    favorites,
    sort,
  ]);

  const goInto = (folderName) => {
    const nextPrefix = currentPrefix
      ? `${currentPrefix}/${folderName}`
      : folderName;
    dlog("goInto", folderName, "->", nextPrefix);
    setCurrentPrefix(nextPrefix);
  };

  const goBreadcrumb = (to) => {
    dlog("goBreadcrumb", to);
    setCurrentPrefix(to);
  };
  const idForUrl = (url) => {
    try {
      return btoa(encodeURIComponent(url));
    } catch {
      return String(url).replace(/[^a-z0-9]/gi, "_");
    }
  };
  const toggleFavorite = async (file) => {
    const url = file?.url || file;
    const path = file?.path;
    const key = file?.urlNorm || normUrl(url);
    const willFav = !favorites.has(key);
    dlog("toggleFavorite", { file, url, key, path, willFav });
    // optimistic UI — normalized URL only
    setFavorites((prev) => {
      const next = new Set(prev);
      if (willFav) next.add(key);
      else next.delete(key);
      dlog("setFavorites (toggleFavorite optimistic)", Array.from(next));
      return next;
    });
    try {
      if (!user) return;
      const uid = user.uid;
      const id = idForUrl(path || url);
      await setDoc(
        doc(db, "imageFolders", uid, "items", id),
        { url, path, favorite: willFav },
        { merge: true }
      );
    } catch (e) {
      console.warn("[RecentlyUploaded] favorite toggle failed, reverting", e);
      // revert
      setFavorites((prev) => {
        const next = new Set(prev);
        if (willFav) next.delete(key);
        else next.add(key);
        dlog("setFavorites (toggleFavorite revert)", Array.from(next));
        return next;
      });
    }
  };
  const pickFile = (file) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Prefer stored physical dimensions (if present), otherwise derive from pixels
      const metaW = file?.widthIn != null ? Number(file.widthIn) : null;
      const metaH = file?.heightIn != null ? Number(file.heightIn) : null;
      const hasMetaDims =
        metaW != null &&
        !Number.isNaN(metaW) &&
        metaW > 0 &&
        metaH != null &&
        !Number.isNaN(metaH) &&
        metaH > 0;

      const widthIn = hasMetaDims
        ? roundSize(metaW)
        : roundSize(img.width / DPI);
      const heightIn = hasMetaDims
        ? roundSize(metaH)
        : roundSize(img.height / DPI);
      onSelect &&
        onSelect({
          src: file.url,
          name: file.name,
          fromRecent: true,
          widthIn,
          heightIn,
          image: img,
        });
      resetAndClose();
    };
    img.onerror = () => {
      console.warn(
        "Could not load image metadata, selecting without dimensions"
      );
      onSelect &&
        onSelect({ src: file.url, name: file.name, fromRecent: true });
      resetAndClose();
    };
    img.src = file.url;
  };

  if (!open) return null;

  dlog("render", { open });

  return (
    <div className="dtf-modal" onClick={resetAndClose}>
      <div className="dtf-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="dtf-modal__head">
          <div style={{ fontWeight: 800 }}>Select Recent Images</div>
          <button
            className="dtf-close"
            onClick={() => {
              dlog("head close clicked");
              resetAndClose();
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <ul className="recent-folderList">
          <li key="__favorites">
            <button
              className={`recent-folder ${selectedFolder === "__FAVORITES__" ? "is-active" : ""}`}
              onClick={() => {
                dlog("render folder", "__FAVORITES__");
                setSelectedFolder("__FAVORITES__");
              }}
            >
              <HiStar className="recent-folder__icon" />
              <span className="recent-folder__name">Favorites</span>
            </button>
          </li>
          {folders.filter(Boolean).map((name) => (
            <li key={name}>
              <button
                className={`recent-folder ${selectedFolder === name ? "is-active" : ""}`}
                onClick={() => {
                  dlog("render folder", name);
                  setSelectedFolder(name);
                }}
              >
                <HiFolder className="recent-folder__icon" />
                <span className="recent-folder__name">{name}</span>
              </button>
            </li>
          ))}
        </ul>

        <div
          className="dtf-modal__search"
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <input
            className="dtf-input"
            placeholder="Search files"
            value={search}
            onChange={(e) => {
              dlog(
                "search box onChange",
                e.target.value,
                "favorites.size",
                favorites.size
              );
              setSearch(e.target.value);
            }}
            style={{ flex: 1 }}
          />
          <div className="cu-cell cu-cell--action" ref={sortWrapRef}>
            <button
              type="button"
              className="ihx-btn ihx-btn--ghost ihx-btn--icon"
              onClick={() => setSortOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={sortOpen}
            >
              <span className="ihx-icon">
                Sort:{" "}
                {sort === "newest"
                  ? "Newest"
                  : sort === "oldest"
                    ? "Oldest"
                    : sort === "az"
                      ? "A–Z"
                      : "Z–A"}
              </span>
            </button>
            {sortOpen && (
              <ul className="ihx-actionMenu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("newest");
                      setSortOpen(false);
                    }}
                  >
                    Newest
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("oldest");
                      setSortOpen(false);
                    }}
                  >
                    Oldest
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("az");
                      setSortOpen(false);
                    }}
                  >
                    A–Z
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSort("za");
                      setSortOpen(false);
                    }}
                  >
                    Z–A
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="dtf-grid">
          {error && (
            <div className="dtf-toast" role="alert">
              <div>⚠️ {error}</div>
              <button className="ihx-btn" onClick={() => setCurrentPrefix("")}>
                Go to Home
              </button>
            </div>
          )}

          {!error && (
            <section className="recent-content">
              <div className="recent-grid">
                {/* Files */}
                {filtered.files.map((file) => {
                  dlog("render file", file.name, {
                    url: file.url,
                    fav: isFav(file),
                  });
                  return (
                    <div
                      key={file.name}
                      className="recent-card"
                      onClick={() => pickFile(file)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") && pickFile(file)
                      }
                    >
                      <div className="recent-card__thumb">
                        <button
                          type="button"
                          className={`recent-favBtn ${isFav(file) ? "is-on" : ""}`}
                          title={
                            isFav(file) ? "Unfavorite" : "Add to Favorites"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            dlog("favBtn clicked for", file.name);
                            toggleFavorite(file);
                          }}
                        >
                          {isFav(file) ? "★" : "☆"}
                        </button>
                        <img src={file.url} alt={file.name} loading="lazy" />
                      </div>
                      <div className="recent-card__meta">
                        <div className="recent-filename" title={file.name}>
                          {file.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {!loading &&
            !error &&
            filtered.folders.length === 0 &&
            filtered.files.length === 0 && (
              <div className="dtf-empty">No items match your search.</div>
            )}

          {loading && <div className="dtf-empty">Loading…</div>}
        </div>

        <div className="dtf-modal__foot">
          <button className="ihx-btn" onClick={resetAndClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
